import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FaCalendarCheck,
  FaFileInvoice,
  FaRedo,
  FaSearch,
  FaSignInAlt,
  FaSignOutAlt,
} from 'react-icons/fa';
import API_BASE_URL, {
  getAdminAuthHeaders,
  handleAdminAuthError,
  isAdminAuthenticated,
} from '../api';
import { formatDisplayDate, toDateString } from '../utils/dateUtils';

const PAGE_SIZE = 10;

const todayString = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addMonthsString = (months) => {
  const date = new Date();
  date.setMonth(date.getMonth() + months);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getDefaultFilters = () => ({
  dateFilter: 'checkIn',
  startDate: todayString(),
  endDate: addMonthsString(1),
  paymentStatus: 'all',
  bookingStatus: 'all',
  sortBy: 'createdAt',
  sortDirection: 'desc',
});

const formatAmount = (amount) => Number(amount || 0).toLocaleString('en-IN');

const statusLabel = (booking) => {
  if (booking.status === 'Cancelled') return 'Cancelled';
  if (booking.isCheckedOut) return 'Checked out';
  if (booking.isCheckedIn) return 'In house';
  return 'Upcoming';
};

const statusClass = (booking) => {
  if (booking.status === 'Cancelled') return 'bg-red-100 text-red-700';
  if (booking.isCheckedOut) return 'bg-slate-200 text-slate-700';
  if (booking.isCheckedIn) return 'bg-blue-100 text-blue-700';
  return 'bg-amber-100 text-amber-800';
};

const Bookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingId, setUpdatingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [filters, setFilters] = useState(getDefaultFilters);

  const fetchBookings = useCallback(async () => {
    if (!isAdminAuthenticated()) {
      navigate('/login', { replace: true });
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API_BASE_URL}/bookings`, {
        headers: getAdminAuthHeaders(),
        params: {
          page,
          limit: PAGE_SIZE,
          searchQuery,
          ...filters,
        },
      });

      const payload = response.data || {};
      const fetchedBookings = Array.isArray(payload) ? payload : payload.bookings || [];

      setBookings((prevBookings) =>
        page === 1 ? fetchedBookings : [...prevBookings, ...fetchedBookings]
      );
      setHasMore(payload.pagination?.hasMore ?? fetchedBookings.length === PAGE_SIZE);
    } catch (fetchError) {
      if (handleAdminAuthError(fetchError, navigate)) return;
      setError(fetchError.response?.data?.error || 'Failed to load bookings.');
    } finally {
      setLoading(false);
    }
  }, [filters, navigate, page, searchQuery]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const summary = useMemo(() => {
    return bookings.reduce(
      (acc, booking) => {
        acc.total += 1;
        if (booking.paymentStatus === 'pending') acc.pending += 1;
        if (booking.isCheckedIn && !booking.isCheckedOut) acc.inHouse += 1;
        if (toDateString(booking.checkInDate) === todayString()) acc.todayArrivals += 1;
        return acc;
      },
      { total: 0, pending: 0, inHouse: 0, todayArrivals: 0 }
    );
  }, [bookings]);

  const resetToFirstPage = () => setPage(1);

  const updateFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    resetToFirstPage();
  };

  const handleSearch = (event) => {
    event.preventDefault();
    setSearchQuery(searchInput.trim());
    resetToFirstPage();
  };

  const handleRefresh = () => {
    setSearchInput('');
    setSearchQuery('');
    setFilters(getDefaultFilters());
    setMessage('');
    resetToFirstPage();
  };

  const updateBooking = async (booking, payload, successMessage) => {
    setUpdatingId(booking.bookingId);
    setError('');
    setMessage('');

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/bookings/update-status/${booking.bookingId}`,
        payload,
        { headers: getAdminAuthHeaders() }
      );

      setBookings((prevBookings) =>
        prevBookings.map((item) =>
          item.bookingId === booking.bookingId ? { ...item, ...response.data.booking } : item
        )
      );
      setMessage(successMessage);
    } catch (updateError) {
      if (handleAdminAuthError(updateError, navigate)) return;
      setError(updateError.response?.data?.error || 'Failed to update booking.');
    } finally {
      setUpdatingId('');
    }
  };

  const handleCheckIn = (booking) => {
    const ok = window.confirm(`Check in ${booking.customerName} for ${booking.roomType}?`);
    if (!ok) return;
    updateBooking(
      booking,
      { isCheckedIn: true, paymentStatus: 'successful' },
      `${booking.customerName} checked in.`
    );
  };

  const handleCheckOut = (booking) => {
    const ok = window.confirm(`Check out ${booking.customerName}?`);
    if (!ok) return;
    updateBooking(booking, { isCheckedOut: true }, `${booking.customerName} checked out.`);
  };

  const navigateToInvoice = (booking) => {
    const invoiceBooking = JSON.stringify(booking);
    sessionStorage.setItem('lastInvoiceBooking', invoiceBooking);
    localStorage.setItem('lastInvoiceBooking', invoiceBooking);

    const invoiceWindow = window.open('/invoice?flow=admin', '_blank');
    if (invoiceWindow) {
      invoiceWindow.opener = null;
      invoiceWindow.focus();
    } else {
      setError('Please allow pop-ups for this site to open invoice in a new tab.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4 text-slate-900 md:p-6">
      <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-yellow-600">
            Front Desk
          </p>
          <h1 className="mt-1 text-3xl font-bold">Bookings</h1>
          <p className="mt-1 text-sm text-slate-600">
            Showing the next month by default, with newest bookings first.
          </p>
        </div>

        <form onSubmit={handleSearch} className="flex w-full flex-col gap-2 sm:flex-row xl:w-[520px]">
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Booking ID, guest, phone, room..."
              className="h-11 w-full rounded-md border border-slate-300 bg-white pl-10 pr-3 text-sm outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100"
            />
          </div>
          <button
            type="submit"
            className="rounded-md bg-yellow-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-yellow-400"
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            <FaRedo /> Reset
          </button>
        </form>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-4">
        <SummaryCard label="Loaded Bookings" value={summary.total} />
        <SummaryCard label="Today Arrivals" value={summary.todayArrivals} />
        <SummaryCard label="In House" value={summary.inHouse} />
        <SummaryCard label="Pending Payments" value={summary.pending} />
      </div>

      <div className="mb-5 rounded-md border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
          <SelectField
            label="Date Type"
            value={filters.dateFilter}
            onChange={(value) => updateFilter('dateFilter', value)}
            options={[
              { value: 'checkIn', label: 'Check-in' },
              { value: 'checkOut', label: 'Check-out' },
            ]}
          />
          <InputField
            label="From"
            type="date"
            value={filters.startDate}
            onChange={(value) => updateFilter('startDate', value)}
          />
          <InputField
            label="To"
            type="date"
            value={filters.endDate}
            onChange={(value) => updateFilter('endDate', value)}
          />
          <SelectField
            label="Payment"
            value={filters.paymentStatus}
            onChange={(value) => updateFilter('paymentStatus', value)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'pending', label: 'Pending' },
              { value: 'successful', label: 'Successful' },
            ]}
          />
          <SelectField
            label="Booking Status"
            value={filters.bookingStatus}
            onChange={(value) => updateFilter('bookingStatus', value)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'Pay at Hotel', label: 'Pay at Hotel' },
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'Cancelled', label: 'Cancelled' },
            ]}
          />
          <SelectField
            label="Sort"
            value={`${filters.sortBy}:${filters.sortDirection}`}
            onChange={(value) => {
              const [sortBy, sortDirection] = value.split(':');
              setFilters((prev) => ({ ...prev, sortBy, sortDirection }));
              resetToFirstPage();
            }}
            options={[
              { value: 'createdAt:desc', label: 'Latest booking' },
              { value: 'checkInDate:desc', label: 'Check-in newest' },
              { value: 'checkInDate:asc', label: 'Check-in oldest' },
              { value: 'checkOutDate:desc', label: 'Check-out newest' },
              { value: 'amount:desc', label: 'Amount high-low' },
            ]}
          />
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-2">
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
        {message && (
          <p className="rounded-md bg-green-50 px-3 py-2 text-sm font-medium text-green-700">
            {message}
          </p>
        )}
      </div>

      <div className="rounded-md border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[1180px] w-full border-collapse text-sm">
            <thead>
              <tr className="bg-slate-900 text-left text-white">
                <th className="p-3 font-semibold">Guest</th>
                <th className="p-3 font-semibold">Stay</th>
                <th className="p-3 font-semibold">Room</th>
                <th className="p-3 font-semibold">Booking ID</th>
                <th className="p-3 font-semibold">Amount</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && page === 1 ? (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-slate-500">
                    Loading bookings...
                  </td>
                </tr>
              ) : bookings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-slate-500">
                    No bookings found for these filters.
                  </td>
                </tr>
              ) : (
                bookings.map((booking) => {
                  const disabled = updatingId === booking.bookingId;
                  const isCancelled = booking.status === 'Cancelled';

                  return (
                    <tr key={booking.id} className="border-t border-slate-200 hover:bg-slate-50">
                      <td className="p-3 align-top">
                        <p className="font-semibold">{booking.customerName}</p>
                        <p className="text-xs text-slate-500">{booking.phoneNumber}</p>
                        {booking.email && <p className="text-xs text-slate-500">{booking.email}</p>}
                      </td>
                      <td className="p-3 align-top">
                        <div className="flex items-start gap-2">
                          <FaCalendarCheck className="mt-1 text-blue-500" />
                          <div>
                            <p className="font-medium">{formatDisplayDate(booking.checkInDate)}</p>
                            <p className="text-slate-500">to {formatDisplayDate(booking.checkOutDate)}</p>
                            <p className="text-xs text-slate-500">{booking.nights || 1} night(s)</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 align-top">
                        <p className="font-medium">{booking.roomType}</p>
                        <p className="text-xs text-slate-500">Meal: {booking.mealPlan || 'N/A'}</p>
                        <p className="text-xs text-slate-500">
                          Guests: {booking.adults || 0} adult(s), {booking.kids || 0} kid(s)
                        </p>
                      </td>
                      <td className="max-w-[210px] p-3 align-top">
                        <p className="break-words font-mono text-xs text-slate-700">
                          {booking.bookingId || 'N/A'}
                        </p>
                      </td>
                      <td className="p-3 align-top">
                        <p className="text-lg font-bold text-slate-900">Rs. {formatAmount(booking.amount)}</p>
                        <p className={booking.paymentStatus === 'pending' ? 'text-xs font-semibold text-amber-700' : 'text-xs font-semibold text-green-700'}>
                          {booking.paymentStatus || 'N/A'}
                        </p>
                      </td>
                      <td className="p-3 align-top">
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusClass(booking)}`}>
                          {statusLabel(booking)}
                        </span>
                      </td>
                      <td className="p-3 align-top">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => navigateToInvoice(booking)}
                            className="inline-flex items-center gap-2 rounded-md bg-purple-600 px-3 py-2 text-xs font-semibold text-white hover:bg-purple-700"
                          >
                            <FaFileInvoice /> Invoice
                          </button>
                          {!isCancelled && !booking.isCheckedIn && (
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => handleCheckIn(booking)}
                              className="inline-flex items-center gap-2 rounded-md bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaSignInAlt /> Check-In
                            </button>
                          )}
                          {!isCancelled && booking.isCheckedIn && !booking.isCheckedOut && (
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => handleCheckOut(booking)}
                              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaSignOutAlt /> Check-Out
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {hasMore && bookings.length > 0 && (
        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={() => setPage((prevPage) => prevPage + 1)}
            disabled={loading}
            className="rounded-md bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value }) => (
  <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
    <p className="text-sm font-medium text-slate-500">{label}</p>
    <p className="mt-1 text-3xl font-bold">{value}</p>
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const InputField = ({ label, value, onChange, type = 'text' }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100"
    />
  </label>
);

export default Bookings;
